import { useState, useCallback } from "react";
import UserService from "@services/UserService";
import ICatalog_ItemUI from "@models/ICatalog_ItemUI";
import ICatalog from "@models/ICatalog";
import IPrice_Group from "@models/IPrice_Group";
import ICategory from "@models/ICategory";
import IVendor_Source from "@models/IVendor_Source";
import IColumns from "@models/IColumns";
import SortOrderEnum from "@models/SortOrderEnum";
import { isNil } from "lodash-es";
import { useAppContextNonNull } from "@contexts/AppContext";

interface UseCatalogOverviewProps {
    catalogGuid: string;
}

export interface CatalogOverviewState {
    catalog_items: ICatalog_ItemUI[];
    totalCatalogItems: number;
    queryString?: string;
    offset: number;
    limit: number;
    catalogModel?: ICatalog;
    priceGroups?: IPrice_Group[];
    categories?: ICategory[];
    vendorSources: IVendor_Source[];
    columns: IColumns;
    sortCol: string;
    sortDir: SortOrderEnum;
    isLoading: boolean;
    isDataFetching: boolean;
    isInitialData: boolean;
    isSmallTable: boolean;
    isPhotoView: boolean;
    deleteElem: string | null;
    sourceCatalogItem?: ICatalog_ItemUI;
    sourceElem?: HTMLElement | null;
    syncElasticSearch?: boolean;
    showOrderRowConfig?: boolean;
    showInventoryRowConfig?: boolean;
    qbdItemSyncActive?: boolean;
    qbdMapperVisible?: boolean;
    extendedInventoryView?: boolean;
    categoryGuid?: string;
}

export const useCatalogOverview = ({ catalogGuid }: UseCatalogOverviewProps) => {
    const { userModel, supplierModel } = useAppContextNonNull();

    const currentToken = UserService.getCurrentToken(userModel);
    const userCols = currentToken?.settings?.catalog_cols || {};

    const getColumnChecked = (objProp: string, defaultVal: boolean) => {
        let isChecked = defaultVal;
        if (!isNil(userCols) && !isNil(userCols[objProp])) {
            isChecked = userCols[objProp];
        }
        return isChecked;
    };

    const [state, setState] = useState<CatalogOverviewState>({
        catalog_items: [],
        totalCatalogItems: 0,
        offset: 0,
        limit: 10,
        vendorSources: [],
        sortCol: "name",
        sortDir: SortOrderEnum.ASC,
        isLoading: true,
        isDataFetching: false,
        isInitialData: true,
        isSmallTable: true,
        isPhotoView: false,
        deleteElem: null,
        extendedInventoryView: false,
        columns: {
            actions: { name: "actions", label: "", isChecked: getColumnChecked("actions", true), notVisible: true },
            active: { name: "active", label: "active", isChecked: getColumnChecked("active", true) },
            category: { name: "category", label: "category", isChecked: getColumnChecked("category", true) },
            type: { name: "type", label: "type", isChecked: getColumnChecked("type", true) },
            name: { name: "name", label: "name", isChecked: getColumnChecked("name", true) },
            sku: { name: "sku", label: "sku", isChecked: getColumnChecked("sku", true) },
            inventory: { name: "inventory", label: "inventory", isChecked: getColumnChecked("inventory", true) },
            short_description: {
                name: "short_description",
                label: "short_description",
                isChecked: getColumnChecked("short_description", true),
            },
            description: { name: "description", label: "description", isChecked: getColumnChecked("description", true) },
            msrp: { name: "msrp", label: "msrp", isChecked: getColumnChecked("msrp", true) },
            map: { name: "map", label: "map", isChecked: getColumnChecked("map", true), notVisible: !supplierModel.has_map },
            pricing: { name: "price_groups", label: "price_groups", isChecked: getColumnChecked("pricing", true) },
            uom: { name: "unit_of_measurement", label: "uom", isChecked: getColumnChecked("uom", true) },
            integrations: {
                name: "integrations",
                label: "integrations",
                isChecked: getColumnChecked("integrations", false),
                notVisible: false,
            },
        },
    });

    const updateState = useCallback((updates: Partial<CatalogOverviewState>) => {
        // console.log("updateState", updates);
        setState((prev) => ({ ...prev, ...updates }));
    }, []);

    // useEffect(() => {
    //     console.log("state", state);
    // }, [state]);

    return {
        state,
        updateState,
    };
};
